import { createSelector } from "reselect"
import { get, chain, isFinite, pickBy } from "lodash"
import { getPortfolioPositionsSort, hasReferenceEquity } from "./positions"
import { getDisplayType, getFactors as getRawFactorsMetadata } from "modules/analyze-historical-exposure/selectors/items"
import { percentageFormatter, factorFormatter, makeCurrencyFormatter, getCurrencySymbol } from "helpers/formatters/numbers"
import { getFactorShortLabel } from "helpers/factors"
import { getStyleExposureAggregation, getCurrency, getMustAggregateFactors } from "modules/portfolio-root/selectors/portfolios"
import { AGGREGATION_FUNCTIONS } from "helpers/factorClassification"

function factorBillionsFormatter(value) {
  if (value) {
    value /= 1000
  }
  return factorFormatter(value)
}

const getFactorValue = (position, factorId) => get(position, `factors.${factorId}`)
const getDescriptorValue = (position, factorId) => get(position, `descriptors.${factorId}`)
const getPositionValue = (position, factorId) => get(position, factorId)

const getPositionsDefinitions = createSelector(
  [hasReferenceEquity, getCurrency],
  (hasReferenceEquity, currency) => ({
    size: {
      label: `Pos Size (${getCurrencySymbol(currency)})`,
      formatter: (value, factor, csv) => {
        if (!isFinite(value)) return null
        if (csv) {
          return parseFloat(value)
        } else {
          return makeCurrencyFormatter(currency, { displayCurrencySymbol: false })(value)
        }
      },
    },
    percEquity: {
      label: hasReferenceEquity ? "% Equity" : "% GMV",
      formatter: value => isFinite(value) ? percentageFormatter(value) : null,
    },
  }),
)

export function getFactorFormatter(factors, id) {
  const isBillions = get(factors, `${id}.isBillions`, false)
  return isBillions ? factorBillionsFormatter : factorFormatter
}

const getFactorsMetadata = createSelector(
  [getRawFactorsMetadata, getStyleExposureAggregation, getMustAggregateFactors],
  (factorsMetadata, styleExposureAggregation, mustAggregateFactors) => styleExposureAggregation !== AGGREGATION_FUNCTIONS.NONE && mustAggregateFactors
    ? pickBy(factorsMetadata, factor => factor.isAggregated)
    : factorsMetadata,

)

const getSelectedGroups = createSelector(
  [getFactorsMetadata],
  (factorsMetadata) => {
    const factors = chain(factorsMetadata)
      .toPairs()
      .filter(([, { noLitmus }]) => !noLitmus)
      .map(([id]) => id)
      .sort()
      .value()

    return [
      { id: "descriptors", label: "Descriptors", descriptors: [], positions: ["size", "percEquity"] },
      { id: "factors", label: "Factors", factors },
    ]
  },
)

export const getGroups = createSelector(
  [getSelectedGroups],
  (groups) => groups.map(group => ({
    id: group.id,
    label: group.label,
    width: group.id === "descriptors" ? group.descriptors.length + group.positions.length : group.factors.length,
  })),
)

export const getFactors = createSelector(
  [getSelectedGroups, getFactorsMetadata, getPortfolioPositionsSort, getDisplayType, getPositionsDefinitions],
  (groups, factorsMetadata, sort, displayType, positionsDefinitions) => chain(groups)
    .map(group => ({
      descriptors: get(group, "descriptors", []).map(factor => ({
        type: "descriptor",
        id: factor,
        label: factor,
        getValue: getDescriptorValue,
        formatter: value => value,
        sort: sort.type === "descriptor" && sort.id === factor ? sort.direction : undefined,
      })),
      positions: get(group, "positions", []).map(factor => ({
        type: "position",
        id: factor,
        label: positionsDefinitions[factor].label,
        getValue: getPositionValue,
        formatter: positionsDefinitions[factor].formatter,
        sort: sort.type === "position" && sort.id === factor ? sort.direction : undefined,
      })),
      factors: chain(group)
        .get("factors", [])
        .map(factor => {
          const { category } = factorsMetadata[factor]

          if (category === displayType) {
            return ({
              type: "factor",
              id: factor,
              label: getFactorShortLabel(factorsMetadata, factor),
              getValue: getFactorValue,
              formatter: getFactorFormatter(factorsMetadata, factor),
              sort: sort.type === "factor" && sort.id === factor ? sort.direction : undefined,
            })
          }

          return false
        })
        .filter(factor => factor)
        .value(),
    }))
    .map(group => [...group.factors, ...group.descriptors, ...group.positions])
    .flatten()
    .value(),
)
